import { BodyText, Button, Separator, Skeleton, toast } from "@/components/ui";
import { ServiceRequest, getId } from "@curai/js-fhir-tools";

import { TRACKERS } from "@/constants/Trackers";
import { useLabOrderEntryActions } from "@/features/Charting/components/Labs/network/useLabOrderEntryActions";
import { useSubmitOrder } from "@/features/Charting/components/Labs/network/useSubmitOrder";
import { trackEvent } from "@/lib/tracking/tracking";
import { OrderDetails } from "./OrderDetails";
import { FailedOrder } from "./components/FailedOrder";
import { ResultsDataGrid } from "./components/InlineResults/ResultsDataGrid";
import { ManualResulting } from "./components/ManualResulting/ManualResulting";
import { UploadOrderRequisition } from "./components/ManualResulting/UploadOrderRequisition";
import { ResultsReviewForm } from "./components/Triage/ResultsReviewForm";
import { ResultsReviewHistory } from "./components/Triage/ResultsReviewHistory";
import { useLabRelatedResources } from "./network/useLabRelatedResources";
import {
  type FailedLabTestGroupItem,
  type LabTestGroupItem,
  LabTestWorkflowStatus,
} from "./network/useLabTests";
import { useOrderResultHistoryAvailableActions } from "./network/useOrderHistoryAvailableActions";

function isFailedLabTestGroupItem(
  lab: LabTestGroupItem | FailedLabTestGroupItem,
): lab is FailedLabTestGroupItem {
  return lab.workflowStatus === LabTestWorkflowStatus.FAILED;
}

export const LabTest = ({
  lab,
  patientId,
}: { lab: LabTestGroupItem | FailedLabTestGroupItem; patientId: string }) => {
  const { data: relatedResources, isPending } = useLabRelatedResources({
    patientId,
    serviceRequestId: lab.serviceRequest.id as string,
  });
  const { data: availableActions } =
    useOrderResultHistoryAvailableActions(patientId);

  const {
    data: { labSubmissionAvailable } = {},
    isFetching: isFetchingLabOrderEntryActions,
  } = useLabOrderEntryActions({
    patientId: patientId,
  });

  const { mutateAsync: submitOrder, isPending: isSubmittingOrder } =
    useSubmitOrder({
      patientId,
      encounterId: relatedResources?.requestGroup?.encounter?.id as string,
      requestGroupId: relatedResources?.requestGroup?.id as string,
      markOnHold: !labSubmissionAvailable,
      serviceRequestId: lab.serviceRequest.id as string,
    });

  if (isPending) {
    return <Skeleton className="h-6" />;
  }

  if (!relatedResources) {
    return <BodyText size="sm">No related resources found</BodyText>;
  }

  // Clinically both these scenarios are "submission", but...
  //
  // If an order is needs submission and active, it means the patient has paid for the labs,
  // and a provider now needs to actually order them. It will either be marked as on-hold (non-HG),
  // or submitted to the lab (HG) depending if HG labs is launched.
  //
  // If an order is needs submission and on-hold, it means the patient has paid for non-HG labs,
  // the provider "submitted" them (marked as on-hold from above), and now a CA needs to "submit"
  // them to Quanum by hand.
  //
  // Once I launch HG labs I can delete the needsQuanumEntry variant of needs submission.
  // Am i done with labs yet (╥_╥)
  const needsSubmission =
    lab.workflowStatus === LabTestWorkflowStatus.NEEDS_SUBMISSION &&
    lab.serviceRequest.status === ServiceRequest.Status.ACTIVE;
  const needsQuanumEntry =
    lab.workflowStatus === LabTestWorkflowStatus.NEEDS_SUBMISSION &&
    lab.serviceRequest.status === ServiceRequest.Status.ON_HOLD;

  const handleSubmitOrder = async () => {
    trackEvent(TRACKERS.SUBMIT_LAB_ORDER);
    try {
      await submitOrder();
    } catch {
      toast.error("An error occurred submitting the order.");
    }
  };

  return (
    <div className="grid gap-4">
      {needsQuanumEntry && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          <BodyText size="sm">
            This order is on-hold and needs to be ordered manually in{" "}
            <BodyText
              size="sm"
              textColor="link"
              className="cursor-pointer"
              underline
              onClick={() => {
                window.open(
                  "https://portal.care360.com/care360-tap/care360.login",
                  "_blank",
                );
              }}
            >
              Quanum Patient 360
            </BodyText>
          </BodyText>
          {availableActions?.manualSubmissionAllowed && (
            <>
              <Separator />
              <UploadOrderRequisition
                patientId={patientId}
                requestGroupId={relatedResources.requestGroup?.id as string}
                serviceRequestId={lab.serviceRequest.id as string}
              />
            </>
          )}
        </div>
      )}
      {needsSubmission && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          <BodyText size="sm">
            This order has been paid for and is ready to be ordered.
          </BodyText>
          <Button
            onClick={handleSubmitOrder}
            isLoading={isSubmittingOrder}
            disabled={isFetchingLabOrderEntryActions}
            variant="primary"
            className="w-fit"
            size="xs"
          >
            Submit to Lab
          </Button>
        </div>
      )}
      {lab.workflowStatus === LabTestWorkflowStatus.ORDERED && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
        </div>
      )}
      {lab.workflowStatus === LabTestWorkflowStatus.FAILED &&
        isFailedLabTestGroupItem(lab) && (
          <div className="grid gap-2">
            <FailedOrder
              patientId={patientId}
              serviceRequestId={lab.serviceRequest.id as string}
              requestGroupId={relatedResources.requestGroup?.id as string}
              encounterId={
                getId(relatedResources.requestGroup?.encounter) ||
                getId(lab.serviceRequest.encounter) ||
                ""
              }
              canRetry={lab.canRetry}
              needsRefundIfCancelled={lab.needsRefundIfCancelled}
            />
            <OrderDetails
              diagnosticReport={relatedResources.diagnosticReports?.[0]}
              requestGroup={relatedResources.requestGroup}
            />
          </div>
        )}
      {lab.workflowStatus === LabTestWorkflowStatus.NEEDS_MANUAL_RESULTING && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          {availableActions?.manualResultingAllowed && (
            <>
              <Separator />
              <ManualResulting
                patientId={patientId}
                serviceRequest={relatedResources.serviceRequest}
                existingDiagnosticReport={
                  relatedResources.diagnosticReports?.[0]
                }
              />
            </>
          )}
        </div>
      )}
      {lab.workflowStatus === LabTestWorkflowStatus.NEEDS_DISCUSSION && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          <Separator />
          <ResultsReviewHistory
            serviceRequest={relatedResources.serviceRequest}
            patientId={patientId}
            needsDiscussion={true}
          />
        </div>
      )}
      {lab.workflowStatus === LabTestWorkflowStatus.NEEDS_TRIAGE && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          {availableActions?.triageAllowed && (
            <>
              <Separator />
              <ResultsReviewForm
                patientId={patientId}
                serviceRequestId={lab.serviceRequest.id as string}
                diagnosticReport={relatedResources.diagnosticReports?.[0]}
              />
            </>
          )}
        </div>
      )}
      {lab.workflowStatus === LabTestWorkflowStatus.NEEDS_TRIAGE_PARTIAL && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          {availableActions?.triageAllowed && (
            <>
              <Separator />
              <ResultsReviewForm
                patientId={patientId}
                serviceRequestId={lab.serviceRequest.id as string}
                diagnosticReport={relatedResources.diagnosticReports?.[0]}
              />
            </>
          )}
          {availableActions?.manualResultingAllowed && (
            <>
              <Separator />
              <ManualResulting
                patientId={patientId}
                serviceRequest={relatedResources.serviceRequest}
                existingDiagnosticReport={
                  relatedResources.diagnosticReports?.[0]
                }
              />
            </>
          )}
        </div>
      )}
      {lab.workflowStatus === LabTestWorkflowStatus.COMPLETED && (
        <div className="grid gap-2">
          <OrderDetails
            diagnosticReport={relatedResources.diagnosticReports?.[0]}
            requestGroup={relatedResources.requestGroup}
          />
          <Separator />
          <ResultsReviewHistory
            serviceRequest={relatedResources.serviceRequest}
            patientId={patientId}
          />
        </div>
      )}
      {Boolean(relatedResources.diagnosticReports?.length) && (
        <ResultsDataGrid
          patientId={patientId}
          testCode={lab.testCode}
          requestGroupId={relatedResources.requestGroup?.id}
        />
      )}
    </div>
  );
};
